import { inject, Injectable } from '@angular/core';
import { ReservationSpecialRequestInfo } from '@rx-applications/clientsite/shared/utils';

import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { Observable, of } from 'rxjs';
import { Sheet2CSVOpts, utils, writeFile } from 'xlsx';

import { CustomCurrencyPipe } from '@roomex/rx-components';
import { DAY_MONTH_YEAR, DAY_MONTH_YEAR_DE, FILE_NAME } from '@rx-applications/shared/utils';
import { CURRENCY_FORMAT_DECIMALS } from '@rx-applications/clientsite/shared/currency/data-access';
import { InternationalizationService } from '@rx-applications/clientsite/shared/internationalization/data-access';
import { BookingReport, BookingReportSheet, BookingRoom, ExportOption, OneOfBookingReport } from '@rx-applications/clientsite/bookings/utils';

import { ConfirmationGuest, ConfirmationRoom, ReservationViewResponse } from '@app/layout-app/shared/models';

@Injectable({ providedIn: 'root' })
export class BookingsService {
  readonly internationalizationService = inject(InternationalizationService);
  readonly rxCurrency = inject(CustomCurrencyPipe);

  public exportSheet(data: OneOfBookingReport[], fileExtension: ExportOption, fileName: string = 'export'): Observable<void> {    
    const filename = `${fileName}_${format(new Date(), FILE_NAME)}.${fileExtension}`;
    const usedProps = this.prepareForWorksheet(data, fileExtension);
    const sortedHeader = [
      'ReservationId',
      'CheckInDate',
      'CheckOutDate',
      'NumberOfNights',
      'NumberOfRooms',
      'LeadName',
      'Booker',
      'CurrencyCode',
      'Total',
      'PaymentOutstanding',
      'Status',
      'CreatedOn',
      'Customer Name',
      'Customer Email',
      'Customer Phone',
      'Hotel Name',
      'Hotel CityName',
      'Hotel Address',
      'Hotel PhoneNumber',
      'Guests',
      'NumberOfGuests',
      'Meals',
    ];
    const worksheet = utils.json_to_sheet(usedProps, { cellDates: true, header: sortedHeader });
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'data');
    if (fileExtension === 'xlsx') {
      writeFile(workbook, filename, { compression: true, bookType: fileExtension });
    } else {
      const sheet2CSVOpts: Sheet2CSVOpts = { FS: this.#isGermanLocale() ? ';' : ',' };
      const csv = utils.sheet_to_csv(worksheet, sheet2CSVOpts);
      const blob = new Blob([csv], { type: 'text/plain;charset=UTF-8' });
      saveAs(blob, filename);
    }

    return of(null);
  }

  public formatToSpecialRequestInfo({
    hotelName,
    guid,
    reference,
    rooms,
    checkIn,
    checkOut,
  }: ReservationViewResponse): ReservationSpecialRequestInfo {
    return {
      hotelName,
      reservationId: reference,
      reservationGuid: guid,
      roomsCount: rooms.length,
      guestNames: this.extractGuests(rooms),
      checkIn: new Date(checkIn),
      checkOut: new Date(checkOut),
    };
  }

  /**
   * @description extracts distinct guest names from the rooms as an array
   * @param rooms rooms that contain guest names
   * @return distinct guest names in format [`Firstname Surname`]
   */
  private extractGuests(rooms: ConfirmationRoom[]): string[] {
    return [
      ...new Set(
        rooms.reduce(
          (guestNames: string[], room: ConfirmationRoom) => [
            ...guestNames,
            ...room.adults.map((passenger: ConfirmationGuest) => `${passenger.firstName} ${passenger.lastName}`),
          ],
          []
        )
      ),
    ];
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  private getNestedVal(path: string[], obj: object): string {
    // Get the next "key" in the stack
    let key = path.shift();
    if (key) {
      if (key.slice(-1) === '\n') {
        key = key.replace(/\s+$/, '');
      }

      // Check for bracket notation
      if (key.indexOf('[') > -1) {
        const match = key.match(/(\w+)\[(\d+)\]/);
        if (match !== null) {
          // Get the "next" key
          key = match[2];
          // eslint-disable-next-line no-param-reassign
          obj = obj[match[1]];
        }
      }

      // Return the value if done, otherwise, keep going
      if (obj[key] !== undefined) {
        return path.length === 0 ? obj[key] : this.getNestedVal(path, obj[key]);
      }
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private parseNestedKeys(item: any, parent?: string): string {
    const values = [];
    if (item === null) {
      // eslint-disable-next-line no-param-reassign
      item = '';
    }
    /* eslint-disable valid-typeof */
    for (const key of Object.keys(item)) {
      const val = item[key];
      let retVal = '';
      if (!Array.isArray(val) && typeof val === 'object') {
        retVal = this.parseNestedKeys(val, key);
      } else if (Array.isArray(val) && typeof val[0] !== undefined) {
        const tempArray = [];
        for (let arrKey = 0; arrKey < val.length; arrKey++) {
          const arrVal = val[arrKey];
          if (parent) {
            tempArray.push(this.parseNestedKeys(arrVal, `${parent}.${key}[${arrKey}]`));
          } else {
            tempArray.push(this.parseNestedKeys(arrVal, `${key}[${arrKey}]`));
          }
        }
        retVal = tempArray.join(';');
      } else {
        let escaped;
        if (parent) {
          escaped = `${parent}.${key}`;
        } else {
          escaped = key.toString().replace(/"/g, '""');
        }
        retVal = `"${escaped}"`;
      }

      values.push(retVal);
    }
    return values.join(';');
  }

  prepareForWorksheet(data: OneOfBookingReport[], fileExtension: ExportOption) {
    return data
      .map((row) => {
        const {
          /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
          DateTermsCommence,
          IsNonRefundable,
          IsOnRequest,
          ModifiedOn,
          PaymentMade,
          ReservationGuid,
          ThumbnailUrl,
          HotelId,
          ...rest
        } = row;
        return rest;
      })
      .map((row) => {
        const { Rooms, ...rest } = row;
        return {
          ...rest,
          ...this.#squashRooms(Rooms),
        };
      })
      .map((row) => ({
        ...this.#parseClientFields(row),
      }))
      .map((row) => {
        const {
          Customer: { Name: CustomerName, Email, Phone },
          Hotel: { Name, CityName, Address, PhoneNumber },
          ...rest
        } = row;
        return {
          'Customer Name': CustomerName,
          'Customer Email': Email,
          'Customer Phone': Phone,
          'Hotel Name': Name,
          'Hotel CityName': CityName,
          'Hotel Address': Address,
          'Hotel PhoneNumber': PhoneNumber,
          ...rest,
        };
      })
      .map((row) => {
        const { Username, ...rest } = row;
        return {
          ...rest,
          CheckInDate: new Date(row.CheckInDate),
          CheckOutDate: new Date(row.CheckOutDate),
          CreatedOn: new Date(row.CreatedOn),
          Booker: Username,
        };
      })
      .map((row) => {
        return fileExtension === 'xlsx'
          ? row
          : {
              ...row,
              Total: this.#isGermanLocale()
                ? this.rxCurrency.transform(+row.Total, row.CurrencyCode, '', CURRENCY_FORMAT_DECIMALS, 'de-DE')
                : row.Total,
              PaymentOutstanding: this.#isGermanLocale()
                ? this.rxCurrency.transform(+row.PaymentOutstanding, row.CurrencyCode, '', CURRENCY_FORMAT_DECIMALS, 'de-DE')
                : row.PaymentOutstanding,
              CheckInDate: format(row.CheckInDate, this.#isGermanLocale() ? DAY_MONTH_YEAR_DE : DAY_MONTH_YEAR),
              CheckOutDate: format(row.CheckOutDate, this.#isGermanLocale() ? DAY_MONTH_YEAR_DE : DAY_MONTH_YEAR),
              CreatedOn: format(row.CreatedOn, this.#isGermanLocale() ? DAY_MONTH_YEAR_DE : DAY_MONTH_YEAR),
            };
      });
  }

  #squashRooms(rooms: BookingRoom[]) {
    return {
      NumberOfGuests: this.getNumberOfGuests(rooms),
      Guests: this.getGuests(rooms),
      Meals: this.getMeals(rooms),
    };
  }

  #parseClientFields(row: BookingReportSheet) {
    let rowModified = { ...row };
    for (let i = 1; i <= 10; i += 1) {
      const key = rowModified[`ClientField_Title${i}`];
      const value = rowModified[`ClientField_Value${i}`];
      const clientField = { [key]: value };
      delete rowModified[`ClientField_Title${i}`];
      delete rowModified[`ClientField_Value${i}`];
      if (!!key && !/^\s*$/.test(key)) {
        rowModified = { ...rowModified, ...clientField };
      }
    }
    return rowModified;
  }

  private getParsedSheet(data: BookingReport[]): string[][] {
    let header = '';
    const sheetData = <string[][]>[];

    const exportableData = this.removeProperties(data);
    const filteredData = this.parseClientFields(exportableData);

    for (const item of filteredData) {
      const parsedHeader = `${this.parseNestedKeys(item)}\n`;
      if (parsedHeader.length > header.length) {
        header = parsedHeader;
      }
    }
    const listOfHeaders = header
      .replace(/"/g, '')
      .replace(/(\r\n|\n|\r)/gm, '')
      .split(';');
    const parsedHeaderTitles = this.fromPascalCaseToTitle(listOfHeaders);
    sheetData.push(parsedHeaderTitles);

    for (const row of filteredData) {
      const rowData = [];
      for (const objPath of listOfHeaders) {
        const cellVal = this.getNestedVal(objPath.split('.'), row);
        rowData.push(`${cellVal}`);
      }
      sheetData.push(rowData);
    }
    return sheetData;
  }

  /**
   * @description extracts only exportable properties
   * @param data all table data
   * @return the same data but without some properties
   */
  private removeProperties(data: BookingReport[]): BookingReportSheet[] {
    return data.map((item: BookingReportSheet) => {
      item.Guests = '';
      if (item.Rooms) {
        item.NumberOfGuests = this.getNumberOfGuests(item.Rooms);
        item.Guests = this.getGuests(item.Rooms);
        item.Meals = this.getMeals(item.Rooms);
        delete item.Rooms;
      }

      const keysToRemove = [
        'DateTermsCommence',
        'IsNonRefundable',
        'IsOnRequest',
        'ModifiedOn',
        'PaymentMade',
        'ReservationGuid',
        'ThumbnailUrl',
        'HotelId',
      ];
      keysToRemove.forEach((key) => {
        delete item[key];
      });

      item.Customer = {
        Name: item.Customer.Name,
        Email: item.Customer.Email,
        Phone: item.Customer.Phone,
      };

      item.Hotel = {
        Name: item.Hotel.Name,
        CityName: item.Hotel.CityName,
        Address: item.Hotel.Address,
        PhoneNumber: item.Hotel.PhoneNumber,
      };

      item.CheckInDate = this.formatSheetDate(item.CheckInDate);
      item.CheckOutDate = this.formatSheetDate(item.CheckOutDate);
      item.CreatedOn = this.formatSheetDate(item.CreatedOn);
      if (this.#isGermanLocale()) {
        item.PaymentOutstanding = this.rxCurrency.transform(
          +item.PaymentOutstanding,
          item.CurrencyCode,
          '',
          CURRENCY_FORMAT_DECIMALS,
          'de-DE'
        );
        item.Total = this.rxCurrency.transform(+item.Total, item.CurrencyCode, '', CURRENCY_FORMAT_DECIMALS, 'de-DE');
      }
      return item;
    });
  }

  private getGuests(rooms: BookingRoom[]): string {
    return rooms
      .reduce((acc: string[], room: BookingRoom) => {
        const guests = room.Passengers.map(({ Title, FirstName, Surname }) => {
          return `${Title} ${FirstName} ${Surname}`;
        });
        return acc.concat(guests);
      }, [])
      .join(', ');
  }

  private getNumberOfGuests(rooms: BookingRoom[]): number {
    return rooms.reduce((acc: number, room: BookingRoom) => {
      return acc + room.Passengers.length;
    }, 0);
  }

  private getMeals(rooms: BookingRoom[]): string {
    return rooms.map((room) => room.MealName).join(', ');
  }

  /**
   * @description transforms ClientField_TitleX and ClientField_ValueY into a property with key and value
   * @param rows all booking data
   * @return same booking data without the ClientField_TitleX and ClientField_ValueY
   */
  private parseClientFields(rows: BookingReportSheet[]) {
    return rows.map((row) => {
      let rowModified = { ...row };
      for (let i = 1; i <= 10; i += 1) {
        const key = rowModified[`ClientField_Title${i}`];
        const value = rowModified[`ClientField_Value${i}`];
        const clientField = { [key]: value };
        delete rowModified[`ClientField_Title${i}`];
        delete rowModified[`ClientField_Value${i}`];
        if (!!key && !/^\s*$/.test(key)) {
          rowModified = { ...rowModified, ...clientField };
        }
      }
      return rowModified;
    });
  }

  /**
   * @description transforms from SomethingInPascalCase to Something In Pascal Case
   * @param text using PascalCase
   * @return text separated by spaces
   */
  private fromPascalCaseToTitle(text: string[]): string[] {
    return text.map((t) =>
      t
        .replace(/\./g, '')
        .replace(/([A-Z]+)/g, ' $1')
        .replace(/"/g, '')
        .replace(/^ /, '')
        .replace(/Username/g, 'Booker')
    );
  }

  private formatSheetDate(date: string): string {
    const localeFormat = this.#isGermanLocale() ? DAY_MONTH_YEAR_DE : DAY_MONTH_YEAR;
    return format(new Date(date), localeFormat);
  }

  #isGermanLocale() {
    return this.internationalizationService.getLocaleId().toLocaleLowerCase() === 'de';
  }
}
